/* Crucial Tracking — the shared cart-pairing helpers for ct-product.js
   (purchase ladder) and ct-kit-builder.js (kit builder):
   window.ctKitProperties, the ONE definition of the kit line item
   properties both add surfaces write (with window.ctKitBilling, the ONE
   builder of the plan line's "Billing" sentence — a property the two
   surfaces write only while the theme setting ct_offer_billing_line is
   ticked), and
   window.ctRenderNotificationLines, the cart-notification line rebuild
   both call right after Dawn's cart-notification.renderContents(). Loaded
   on every page (deferred, from layout/theme.liquid's <head>, ahead of the
   section scripts) so the properties are written whatever the cart type;
   the notification helper no-ops outside a notification cart.

   Why: the ladder and the kit builder add a device AND a plan in one
   /cart/add. Dawn's renderContents() injects only the INNER html of the
   line whose key it is handed (the tracker's) into #cart-notification-product,
   so the plan line — the one carrying the included-year offer — never
   shows, and that one line loses its .cart-item wrapper. This helper
   rebuilds the host from every added item's full .cart-item outerHTML
   (the tracker's included), taken from the same Section Rendering API HTML
   that Dawn already fetched (response.sections['cart-notification-product']).
   The lines are shown in the order the cart holds them, never the order
   the add answered in: both surfaces send the plan line first and the
   tracker last, so that the tracker — the newest line — sits above its
   plan in the cart, and the notification reads the same way.

   Kit pairing: a device + plan add writes a `_ct_kit` property on both
   lines, and sections/cart-notification-product.liquid wraps a CONTIGUOUS
   run of a kit's lines in one .ct-kit-group (label + offer pill + lines).
   For an added item carrying `_ct_kit` the group node is used when it holds
   that item's line — pushed once per kit id — and the per-item node is the
   fallback (no group in the HTML, an unpaired line, or a kit whose lines
   are not contiguous, where the group holds the first line and the other
   line follows on its own). A fitted add-on (window.ctFittedProperties,
   the CT-A08's battery set) nested under a kit's tracker is rendered
   inside that kit's group, so its group is used for it as well. Nothing
   added is ever dropped.

   Dawn's open(), focus trap and close() key off .active/.animate and
   transitionend on #cart-notification, never off the product markup, so
   swapping the host's children is safe. If this file fails to load, the
   callers' thin wrappers no-op and Dawn's single-line render stands, and
   the adds carry no kit properties. */

(function () {
  // Kit pairing properties for a device + plan add. The kit id is
  // deterministic from its contents ("ct-v05+real-time-annual"), so a
  // repeat add of the same kit merges into the existing pair and bumps the
  // quantity instead of growing a second "Kit 2". `_ct_kit` and
  // `_ct_kit_label` are underscore-private (hidden at checkout, readable by
  // Liquid, the Ajax API and admin). The plan line carries the visible
  // properties, in the order written here (Shopify prints them in
  // insertion order): "For tracker", the pairing string that survives into
  // checkout, the confirmation email and the customer's order page, then —
  // only when the caller hands one over — "Billing" (see `billing` below,
  // and the theme setting that governs it). Returns null when the device
  // code, the plan key or the plan title is blank — then the add carries no
  // properties, exactly as a tracker-only add does. The device code must
  // come from ct-device-detect on both surfaces (the same upcase +
  // product-title fallback), so the same tracker + plan can never split
  // into two kits. Schema consumers:
  // sections/main-cart-items.liquid, sections/cart-notification-product.liquid,
  // snippets/ct-cart-offer-line.liquid.
  // `addonKey` (optional; October 2026, the CT-A08's fitted battery set):
  // a kit whose tracker ships with a fitted add-on gets its own kit id
  // ("ct-a08+power-saver-annual+battery-set"), so it never merges with the
  // same tracker + plan bought without the add-on. Each kit therefore stays
  // one tracker line plus one plan line, and the kit stepper keeps working.
  // The label is the same either way; the add-on line itself says what it is.
  // `billing` (optional; October 2026): the sentence from
  // window.ctKitBilling below, written as the plan line's second visible
  // property, "Billing", after "For tracker" — never on the tracker line
  // and never on an add-on line.
  // GOVERNED BY A THEME SETTING: settings.ct_offer_billing_line ("Print the
  // billing line on plan lines at checkout", config/settings_schema.json).
  // Each surface's Liquid relays it in its JSON as a boolean
  // (ct-purchase-options: offerBillingLine; ct-kit-builder:
  // offer.billingLine), and both scripts ask for the sentence only while
  // that setting AND the offer are on and the plan has a price. Unticked,
  // the plan line carries `_ct_kit`, `_ct_kit_label` and "For tracker" and
  // nothing else. Why it is a switch: the sentence is frozen at add time
  // and is not corrected if a discount code later displaces the automatic
  // included-year discount at checkout — the order would then say "First
  // 12 months included" in writing while the plan is charged. Ticking the
  // setting accepts that.
  // What it is for: the checkout's order summary prints a line's visible
  // properties under it and, beside them, only "Recurring subtotal $10.00
  // every month": "Billing" puts the included period and what is billed
  // after it on that same screen. It exists for the checkout, the order
  // confirmation and the admin order; the cart page and the cart
  // notification hide it on every row
  // (sections/main-cart-items.liquid, sections/cart-notification-product.liquid),
  // because the cart states the billing live and this sentence is frozen
  // at add time. Blank → no "Billing" property at all (the setting is
  // unticked, the offer is off, or the plan has no price). It is not part
  // of the kit id, and a line's properties are part of what Shopify merges
  // an add into: a repeat add of a kit joins the plan line already in the
  // cart only while the two sentences are identical — which is why there
  // is one builder for it, and why that builder formats the price itself.
  // Where they differ (a cart from before the property, a cart from the
  // other side of a change to the setting, a plan price changed since) the
  // kit holds two plan lines, until its stepper or repair in
  // assets/ct-cart-kit.js puts the whole count back on one.
  if (!window.ctKitProperties) {
    window.ctKitProperties = function (deviceCode, planKey, planTitle, addonKey, billing) {
      var code = String(deviceCode || '').trim();
      var key = String(planKey || '').trim();
      var title = String(planTitle || '').trim();
      var addon = String(addonKey || '').trim();
      var bill = String(billing || '').trim();
      if (!code || !key || !title) return null;
      var kitId = code.toLowerCase() + '+' + key + (addon ? '+' + addon : '');
      var kitLabel = code + ' + ' + title;
      var plan = { _ct_kit: kitId, _ct_kit_label: kitLabel, 'For tracker': code };
      if (bill) plan.Billing = bill;
      return {
        tracker: { _ct_kit: kitId, _ct_kit_label: kitLabel },
        plan: plan,
      };
    };
  }

  // The bundle plan line's "Billing" sentence — built here and nowhere
  // else, its price figure included, so the tracker page (ct-product.js)
  // and the kit builder (ct-kit-builder.js) write the identical string for
  // the same plan:
  //   monthly
  //     "First 12 months included, then $10/mo per tracker from month 13"
  //     ("First month included, then $10/mo per tracker from month 2" when
  //     the monthly offer runs one cycle)
  //   annual
  //     "First year included, then $100/yr per tracker from year 2"
  //     ("First 2 years included, then $100/yr per tracker from year 3"
  //     when the annual offer runs two cycles)
  // "per tracker" always, whatever the quantity: one sentence on a plan
  // line that can hold several plans cannot state a multiplied figure the
  // way the cart's own trail does, and it must read the same for every add
  // (the quantity changes after it is written; repeat adds must merge).
  // Takes { monthly, cycles, cents }. No figure is typed here: `cycles`
  // is the offer's cycle count for the plan's term
  // (settings.ct_offer_cycles_monthly / ct_offer_cycles_annual, relayed in
  // each surface's JSON) and `cents` is what the plan's selling plan bills
  // per cycle, a number of cents: the selling-plan allocation price, else
  // the variant price, which each surface's Liquid resolves the same way
  // and relays in its JSON (ct-purchase-options: the plan's billedPrice;
  // ct-kit-builder: the plan's price).
  // The price is formatted HERE — never by a caller and never by a Liquid
  // money filter: the store's "$" in front, comma thousands, no ".00", two
  // decimals otherwise ("$10", "$10.50", "$1,000"), then "/mo" or "/yr". A
  // price each surface formatted for itself could differ between the two
  // (a shop money format with a suffix, HTML or other separators; a
  // selling plan with a price adjustment, read from the variant on one
  // surface and from the allocation on the other), and the same kit added
  // from both would then hold two plan lines.
  // The callers ask for it only while settings.ct_offer_billing_line and
  // the standing offer are both on and the plan has a price (see `billing`
  // above); SIM plans and platform access alike, never a self-serve plan
  // (those are bought through ct-plan-chooser, which writes no kit
  // properties). "Included", never "free"; no "$0", no saving.
  // Returns '' when `cents` is not a finite number above 0 (a string, a
  // preformatted price, nothing at all) or `cycles` is not a whole number
  // of 1 or more — then the add carries no "Billing" property.
  if (!window.ctKitBilling) {
    window.ctKitBilling = function (plan) {
      var input = plan || {};
      var cycles = Number(input.cycles);
      var cents = typeof input.cents === 'number' ? Math.round(input.cents) : NaN;
      if (!Number.isFinite(cents) || cents <= 0 || !Number.isInteger(cycles) || cycles < 1) return '';
      var parts = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var price = '$' + parts.join('.') + (input.monthly ? '/mo' : '/yr');
      if (input.monthly) {
        return (
          'First ' + (cycles === 1 ? 'month' : cycles + ' months') + ' included, then ' + price + ' per tracker from month ' + (cycles + 1)
        );
      }
      return (
        'First ' + (cycles === 1 ? 'year' : cycles + ' years') + ' included, then ' + price + ' per tracker from year ' + (cycles + 1)
      );
    };
  }

  // Fitted add-on properties (October 2026, the CT-A08's battery set). The
  // add-on line is added right after its tracker and nested under that
  // exact line with parent_line_key (ct-product.js addWithAddon; Shopify's
  // nested cart lines: it sits right
  // after its parent, leaves the cart with it, and checkout shows it under
  // the tracker). Two private properties, never `_ct_kit` (the kit passes
  // treat every non-plan kit line as the tracker):
  //   tracker  { _ct_fitted: addonKey } — a tracker line that ships with the
  //            add-on never merges with the same tracker bought without it,
  //            so the add-on's quantity can always equal its tracker's;
  //   add-on   { _ct_fitted_to: parentRef } — the tracker's kit id, or its
  //            device code on a tracker-only add. It keeps the add-on line's
  //            key unique per parent (identical children of two parents
  //            would otherwise share one key) and marks the line for
  //            assets/ct-cart-addon.js.
  // Returns null when either input is blank — then nothing is fitted.
  if (!window.ctFittedProperties) {
    window.ctFittedProperties = function (addonKey, parentRef) {
      var key = String(addonKey || '').trim();
      var ref = String(parentRef || '').trim();
      if (!key || !ref) return null;
      return { tracker: { _ct_fitted: key }, addon: { _ct_fitted_to: ref } };
    };
  }

  // The notification's summary — "Today $139" and the delivery line — is
  // rendered by sections/cart-notification-product.liquid as a
  // <template data-ct-cart-summary> inside EVERY line (Dawn injects only
  // the added line's inner HTML, so any line must carry it). Whenever the
  // product host changes — Dawn's renderContents or the rebuild below —
  // the first template's content is copied into #ct-cart-notification-summary
  // (snippets/cart-notification.liquid) and the host shown; with no
  // template in the host (an older render, a section without it) the
  // summary is hidden. Same observer arrangement as assets/ct-cart-imei.js.
  function syncNotificationSummary() {
    var host = document.getElementById('cart-notification-product');
    var summary = document.getElementById('ct-cart-notification-summary');
    if (!host || !summary) return;
    var template = host.querySelector('template[data-ct-cart-summary]');
    if (!template) {
      summary.hidden = true;
      summary.innerHTML = '';
      return;
    }
    summary.innerHTML = template.innerHTML;
    summary.hidden = false;
  }

  var summaryObserved = false;
  function watchNotificationSummary() {
    var host = document.getElementById('cart-notification-product');
    if (!host || summaryObserved || !('MutationObserver' in window)) return;
    summaryObserved = true;
    new MutationObserver(syncNotificationSummary).observe(host, { childList: true, subtree: true });
    syncNotificationSummary();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchNotificationSummary);
  } else {
    watchNotificationSummary();
  }

  if (window.ctRenderNotificationLines) return;

  // Liquid's handleize, for the group id fallback: lowercase, runs of
  // anything but a-z / 0-9 become one hyphen, no leading/trailing hyphen.
  function handleize(value) {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function findKitGroup(doc, kit) {
    var groups = doc.querySelectorAll('.ct-kit-group');
    for (var i = 0; i < groups.length; i += 1) {
      if (groups[i].getAttribute('data-ct-kit') === kit) return groups[i];
    }
    return doc.querySelector('[id="cart-notification-kit-' + handleize(kit) + '"]');
  }

  window.ctRenderNotificationLines = function (cart, response) {
    if (!cart || cart.tagName !== 'CART-NOTIFICATION') return;
    var html = response && response.sections && response.sections['cart-notification-product'];
    var host = document.getElementById('cart-notification-product');
    if (!html || !host) return;

    // /cart/add.js answers {items:[...]} for a multi-line add and the bare
    // line item (with its key) for a single-line add.
    var items = Array.isArray(response.items) ? response.items : response.key ? [response] : [];
    if (!items.length) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var picked = [];
    var kitsDone = {};
    items.forEach(function (item) {
      var kit = item.properties && item.properties._ct_kit;
      var node = doc.querySelector('[id="cart-notification-product-' + item.key + '"]');
      // The group stands in for this item only when it holds the item's
      // own line; otherwise the per-item node below keeps it on screen. A
      // fitted add-on nested under a kit's tracker carries no `_ct_kit` but
      // is rendered inside that kit's group, so its enclosing group is used
      // too — whichever of the kit's items comes first pushes the group, once.
      var group = kit ? findKitGroup(doc, kit) : null;
      if (!(group && node && group.contains(node))) group = node ? node.closest('.ct-kit-group') : null;
      if (group) {
        var groupId = group.getAttribute('data-ct-kit') || group.id;
        if (!kitsDone[groupId]) {
          kitsDone[groupId] = true;
          picked.push(group);
        }
        return;
      }
      if (node) picked.push(node);
    });
    if (!picked.length) return;

    // In the order the section prints them — the cart's own, tracker above
    // its plan — not the order of the add's answer (a kit's plan line is
    // sent first). Only seen where the lines are not in one group; a group
    // already holds its lines in cart order.
    picked.sort(function (a, b) {
      if (a === b) return 0;
      return a.compareDocumentPosition(b) & 4 /* DOCUMENT_POSITION_FOLLOWING */ ? -1 : 1;
    });

    host.innerHTML = picked
      .map(function (element) {
        return element.outerHTML;
      })
      .join('');
  };
})();
